import * as React from "react";
import { createPortal } from "react-dom";
import { Search, ChevronDown, MapPin, Plus, Sparkles, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { apiFetch } from "@/lib/api.js";

interface Space {
  id: string;
  name: string;
  type: string;
  parentId: string | null;
}

/** Result of an AI placement suggestion (Flow #3). */
export interface PlacementSuggestionResult {
  suggestedSpaceId: string | null;
  suggestedSpaceName: string | null;
  reasoning: string;
}

interface SearchableSelectProps {
  spaces: Space[];
  value: string | null;
  onChange: (id: string | null) => void;
  onSpaceCreated?: (space: Space) => void;
  placeholder?: string;
  /**
   * Optional AI "suggest a spot" affordance (Flow #3). When provided, a quiet
   * "Suggest a spot" row appears *inside* the dropdown — only once the user has
   * already opened the picker to choose a location. This keeps the default
   * "pick an existing location" flow completely unobstructed: the suggestion is
   * opt-in and one tap away, never a separate button competing for attention.
   * Returns null (or throws) if a suggestion can't be produced.
   */
  onRequestSuggestion?: () => Promise<PlacementSuggestionResult | null>;
}

export function SearchableSelect({ spaces, value, onChange, onSpaceCreated, placeholder = "Select location...", onRequestSuggestion }: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [newType, setNewType] = React.useState<"room" | "container">("room");
  const [saving, setSaving] = React.useState(false);
  const [suggesting, setSuggesting] = React.useState(false);
  const [suggestion, setSuggestion] = React.useState<PlacementSuggestionResult | null>(null);
  const ref = React.useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>({});

  const requestSuggestion = async () => {
    if (!onRequestSuggestion || suggesting) return;
    setSuggesting(true);
    try {
      const res = await onRequestSuggestion();
      // Only keep a suggestion that points at a real, known space.
      if (res?.suggestedSpaceId && spaces.some((s) => s.id === res.suggestedSpaceId)) {
        setSuggestion(res);
      } else {
        setSuggestion(res ?? null);
      }
    } catch {
      // Silent: a suggestion is a nicety, not a blocker. The user can still pick manually.
    }
    setSuggesting(false);
  };

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        const dropdown = document.getElementById("ss-dropdown");
        if (dropdown && dropdown.contains(e.target as Node)) return;
        setOpen(false); setCreating(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  React.useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownStyle({ position: "fixed", top: rect.bottom + 4, left: rect.left, width: rect.width, zIndex: 9999 });
    }
  }, [open]);

  const getPath = (id: string): string => {
    const parts: string[] = [];
    let current: Space | undefined = spaces.find((s) => s.id === id);
    while (current) {
      parts.unshift(current.name);
      current = current.parentId ? spaces.find((s) => s.id === current!.parentId) : undefined;
    }
    return parts.join(" › ");
  };

  const handleCreateSpace = async () => {
    if (!newName.trim() || saving) return;
    setSaving(true);
    try {
      const space = await apiFetch<Space>("/api/spaces", { method: "POST", body: JSON.stringify({ name: newName.trim(), type: newType }) });
      onSpaceCreated?.(space);
      onChange(space.id);
      setOpen(false); setCreating(false); setNewName(""); setSearch("");
    } catch { /* ignore */ }
    setSaving(false);
  };

  const filtered = spaces.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()));
  const selected = value ? spaces.find((s) => s.id === value) : null;

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(!open)} aria-label={selected ? `Location: ${getPath(selected.id)}` : placeholder} aria-expanded={open} aria-haspopup="listbox" className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl border border-neutral-border/40 text-sm text-left focus:outline-none focus:border-primary">
        <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
        <span className={cn("flex-1 truncate", !selected && "text-gray-400")}>{selected ? getPath(selected.id) : placeholder}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
      {open && createPortal(
        <div id="ss-dropdown" style={dropdownStyle} className="bg-white rounded-xl border border-neutral-border/30 shadow-lg max-h-60 overflow-y-auto" role="listbox" aria-label="Location options">
          <div className="p-2 sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." aria-label="Search locations" className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-neutral-border/30 text-xs" />
            </div>
          </div>
          {onRequestSuggestion && !search && (
            <div className="border-b border-neutral-border/20">
              {suggestion ? (
                <div className="px-4 py-2.5 bg-primary/5">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold text-primary">
                    <Sparkles className="w-3.5 h-3.5" />
                    {suggestion.suggestedSpaceId && suggestion.suggestedSpaceName
                      ? `Suggested: ${suggestion.suggestedSpaceName}`
                      : "No confident suggestion"}
                  </div>
                  {suggestion.reasoning && <p className="text-[11px] text-gray-500 mt-1 leading-snug">{suggestion.reasoning}</p>}
                  {suggestion.suggestedSpaceId && (
                    <button
                      type="button"
                      onClick={() => { onChange(suggestion.suggestedSpaceId); setOpen(false); setSearch(""); }}
                      className="mt-2 w-full py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:opacity-90 transition"
                    >
                      Use this spot
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={requestSuggestion}
                  disabled={suggesting}
                  className="w-full flex items-center gap-1.5 px-4 py-2 text-sm text-primary hover:bg-primary/5 disabled:opacity-60"
                >
                  {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {suggesting ? "Thinking…" : "Suggest a spot"}
                </button>
              )}
            </div>
          )}
          <button type="button" role="option" aria-selected={!value} onClick={() => { onChange(null); setOpen(false); setSearch(""); }} className={cn("w-full text-left px-4 py-2 text-sm hover:bg-primary/5", !value && "bg-primary/10 text-primary")}>
            No location
          </button>
          {filtered.map((space) => {
            const isSuggested = suggestion?.suggestedSpaceId === space.id;
            return (
              <button key={space.id} type="button" role="option" aria-selected={value === space.id} onClick={() => { onChange(space.id); setOpen(false); setSearch(""); }} className={cn("w-full text-left px-4 py-2 text-sm hover:bg-primary/5 flex items-center", value === space.id && "bg-primary/10 text-primary")}>
                <span className="flex-1 truncate">{getPath(space.id)}</span>
                {isSuggested && <Sparkles className="w-3 h-3 text-primary ml-1.5 shrink-0" aria-label="Suggested" />}
                <span className="text-[10px] text-gray-400 ml-2 uppercase shrink-0">{space.type}</span>
              </button>
            );
          })}
          <div className="border-t border-neutral-border/20">
            {!creating ? (
              <button type="button" onClick={() => setCreating(true)} className="w-full text-left px-4 py-2 text-sm text-primary hover:bg-primary/5 flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" />Create space
              </button>
            ) : (
              <div className="p-2 space-y-2">
                <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleCreateSpace()} placeholder="Space name" className="w-full px-3 py-1.5 rounded-lg border border-neutral-border/30 text-xs" />
                <div className="flex gap-1">
                  {(["room", "container"] as const).map((t) => (
                    <button key={t} type="button" onClick={() => setNewType(t)} className={cn("px-2 py-1 rounded text-[10px] font-medium", newType === t ? "bg-primary text-white" : "bg-gray-100 text-gray-500")}>{t}</button>
                  ))}
                  <div className="flex-1" />
                  <button type="button" onClick={handleCreateSpace} disabled={!newName.trim() || saving} className="px-3 py-1 rounded bg-primary text-white text-[10px] font-medium disabled:opacity-50">Add</button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
